export const STORAGE_KEY = 'morningMissionConfig_v5';

export const DEFAULT_TASKS = [
  { icon: '👕', title: 'Tag tøj på' },
  { icon: '🥣', title: 'Spis morgenmad' },
  { icon: '🦷', title: 'Børst tænder' },
  { icon: '🎒', title: 'Pak taske' },
  { icon: '👟', title: 'Tag sko på' },
];

export const DEFAULT_CHILDREN = [
  { name: 'Liva', icon: '🐱' },
  { name: 'Astrid', icon: '🦄' },
];

const DEFAULT_START_MESSAGE = 'Tryk start, og begge børn går i gang samtidig.';

const normalizeChildTemplate = (child = {}, index = 0) => ({
  name: String(child.name || DEFAULT_CHILDREN[index]?.name || `Barn ${index + 1}`).trim(),
  icon: String(child.icon || DEFAULT_CHILDREN[index]?.icon || '🙂').trim(),
});

const normalizeTasks = (tasks) => {
  if (!Array.isArray(tasks) || !tasks.length) return clone(DEFAULT_TASKS);
  return tasks
    .map((task, index) => ({
      icon: String(task?.icon || DEFAULT_TASKS[index]?.icon || '⭐').trim(),
      title: String(task?.title || DEFAULT_TASKS[index]?.title || '').trim(),
    }))
    .filter((task) => task.title);
};

export const createRoutine = (input = {}, fallback = {}) => {
  const id = String(input.id || fallback.id || `routine-${Date.now()}`);
  const name = String(input.name || fallback.name || 'Ny rutine').trim();

  return {
    id,
    name,
    title: String(input.title || fallback.title || name || 'Ny rutine').trim(),
    startMessage: String(input.startMessage || fallback.startMessage || DEFAULT_START_MESSAGE).trim(),
    hour: Number.isFinite(Number(input.hour)) ? Math.max(0, Math.min(23, Number(input.hour))) : Number(fallback.hour ?? 7),
    minute: Number.isFinite(Number(input.minute)) ? Math.max(0, Math.min(59, Number(input.minute))) : Number(fallback.minute ?? 0),
    tasks: normalizeTasks(input.tasks ?? fallback.tasks),
    children: (Array.isArray(input.children) && input.children.length ? input.children : fallback.children || DEFAULT_CHILDREN)
      .map((child, index) => normalizeChildTemplate(child, index)),
  };
};

export const DEFAULT_CONFIG = {
  activeRoutineId: 'morning',
  routines: [
    createRoutine({
      id: 'morning',
      title: '🌞 Morgenmission',
      startMessage: DEFAULT_START_MESSAGE,
      hour: 7,
      minute: 0,
      tasks: DEFAULT_TASKS,
      children: DEFAULT_CHILDREN,
    }),
    createRoutine({
      id: 'evening',
      title: '🌙 Aftenmission',
      startMessage: 'Tryk start, og begge børn går i gang med aftenrutinen.',
      hour: 19,
      minute: 0,
      tasks: [
        { icon: '🧸', title: 'Ryd legetøj op' },
        { icon: '🛁', title: 'Tag bad' },
        { icon: '🪥', title: 'Børst tænder' },
        { icon: '📚', title: 'Læsetid' },
        { icon: '🛌', title: 'I seng' },
      ],
      children: DEFAULT_CHILDREN,
    }),
  ],
};

export const getActiveRoutine = (config = DEFAULT_CONFIG) => {
  const routines = Array.isArray(config.routines) && config.routines.length ? config.routines : DEFAULT_CONFIG.routines;
  const routine = routines.find((r) => r.id === config.activeRoutineId) || routines[0] || DEFAULT_CONFIG.routines[0];
  return createRoutine(routine, DEFAULT_CONFIG.routines[0]);
};

export const createRuntimeChildren = (childrenTemplates = DEFAULT_CHILDREN) =>
  (Array.isArray(childrenTemplates) && childrenTemplates.length ? childrenTemplates : DEFAULT_CHILDREN).map((template, index) => {
    const normalized = normalizeChildTemplate(template, index);
    return {
      name: normalized.name,
      avatar: normalized.icon,
      index: -1,
      points: 0,
      taskStart: null,
      done: [],
      lastTaskMs: null,
      wins: 0,
      pointStats: {
        completion: 0,
        first: 0,
        comeback: 0,
        speed: 0,
      },
    };
  });

export const initialChildren = createRuntimeChildren(DEFAULT_CHILDREN);

export const POINT_RULES = {
  completion: 1,
  first: 1,
  comeback: 1,
  speed: 1,
  speedThresholdMs: 120000,
};

export const clone = (v) => JSON.parse(JSON.stringify(v));

export const loadConfig = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return clone(DEFAULT_CONFIG);
    const parsed = JSON.parse(raw);

    if (Array.isArray(parsed.routines) && parsed.routines.length) {
      const routines = parsed.routines.map((routine, index) =>
        createRoutine(
          { ...routine, id: routine?.id || `routine-${index + 1}` },
          DEFAULT_CONFIG.routines[index % DEFAULT_CONFIG.routines.length],
        ),
      );

      const activeRoutineId = routines.some((routine) => routine.id === parsed.activeRoutineId)
        ? parsed.activeRoutineId
        : routines[0].id;

      return {
        activeRoutineId,
        routines,
      };
    }
  } catch {
    return clone(DEFAULT_CONFIG);
  }
};

export const formatTime = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  }

  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

export const nextDeparture = (hour, minute) => {
  const now = new Date();
  const target = new Date();
  target.setHours(hour, minute, 0, 0);
  if (now >= target) target.setDate(target.getDate() + 1);
  return target;
};

export const parseTasks = (text) => {
  const parts = text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const fallback = clone(DEFAULT_TASKS);
  if (!parts.length) return fallback;

  return parts
    .map((part, idx) => {
      const splitAt = part.indexOf('|');
      if (splitAt === -1) return { icon: fallback[idx]?.icon || '⭐', title: part };
      const icon = part.slice(0, splitAt).trim() || fallback[idx]?.icon || '⭐';
      const title = part.slice(splitAt + 1).trim() || fallback[idx]?.title || part;
      return { icon, title };
    })
    .filter((t) => t.title);
};

export const randomPraise = (first) => {
  const firstList = ['🏆 Fantastisk!', '🚀 Superhurtigt!', '✨ Først færdig!', '🌟 Mega flot!'];
  const normalList = ['👏 Godt gået!', '💛 Flot klaret!', '🌈 Sejt arbejde!', '🙌 Vel gjort!'];
  const list = first ? firstList : normalList;
  return list[Math.floor(Math.random() * list.length)];
};

export const getTaskPointBreakdown = ({ first, comeback, elapsedMs }) => {
  const completion = POINT_RULES.completion;
  const firstBonus = first ? POINT_RULES.first : 0;
  const comebackBonus = comeback ? POINT_RULES.comeback : 0;
  const speedBonus = elapsedMs <= POINT_RULES.speedThresholdMs ? POINT_RULES.speed : 0;

  const total = completion + firstBonus + comebackBonus + speedBonus;

  return {
    total,
    completion,
    first: firstBonus,
    comeback: comebackBonus,
    speed: speedBonus,
  };
};

export const mergePointStats = (currentStats, breakdown) => ({
  completion: (currentStats?.completion || 0) + breakdown.completion,
  first: (currentStats?.first || 0) + breakdown.first,
  comeback: (currentStats?.comeback || 0) + breakdown.comeback,
  speed: (currentStats?.speed || 0) + breakdown.speed,
});

export const getPointOverviewLines = (participant, taskCount) => [
  `⭐ Point: +${participant?.points || 0}`,
  `✅ Klaret: ${participant?.done.length || 0}/${taskCount || 0}`,
  `✅ Fuldført: +${participant?.pointStats.completion || 0}`,
  `🏅 Først: +${participant?.pointStats.first || 0}`,
  `⚡ Comeback: +${participant?.pointStats.comeback || 0}`,
  `⏱️ Hurtig bonus: +${participant?.pointStats.speed || 0}`,
];

export const buildTaskFeedback = ({ childName, first, comeback }) => {
  if (comeback) return `⚡ Comeback, ${childName}! Hurtigste tid på opgaven!`;
  return `${randomPraise(first)} ${childName}!`;
};
